import { z } from 'zod';
import { reverseKeys } from './util/reverseKeys.js';
// This should increment any time there is a breaking change between client and server
// Changes to this version will force client side code to refresh
export const RELEASE_VERSION = '0.6.32';
export const imageTypes = ['image/jpg', 'image/jpeg', 'image/png', 'image/webp'];
export const fileTypes = ['text/html', 'text/plain', 'text/markdown', 'text/json'];
export const FeedbackTypesLookup = {
    user: 'User',
    post: 'Post',
    business: 'Business',
    comment: 'Comment'
};
export const DocumentTypesLookup = {
    feedback: 'Feedback',
    affinity: 'Affinity',
    block: 'Block',
    dummy: 'Dummy',
    starredBusiness: 'StarredBusiness',
    report: 'Report',
    adminAction: 'Administrative Action',
    userLogin: 'User Login',
    siteSettings: 'Site Settings',
    pollResponse: 'Poll Response',
    notification: 'Notification',
    emailNotification: 'Email Notification',
    telemetry: 'Telemetry',
    backup: 'Backup',
    userLedger: 'User Ledger',
    post: 'Post',
    comment: 'Comment',
    user: 'User',
    business: 'Business',
    emailLog: 'Email Log',
    postView: 'Post Engagement',
    directMessage: 'Direct Message',
    job: 'Job'
};
export const DocumentTypesEnum = z.nativeEnum(reverseKeys(DocumentTypesLookup));
export const FeedbackTypesEnum = z.nativeEnum(reverseKeys(FeedbackTypesLookup));
export const zDocument = z.object({
    _id: z.string(),
    _rev: z.string(),
    type: DocumentTypesEnum,
    created: z.string().datetime().or(z.literal('')),
    updated: z.string().datetime().or(z.literal('')),
    _attachments: z.any().optional(),
    deleted: z.boolean().optional()
});
/**
 * See https://github.com/colinhacks/zod/issues/372
 * You use this to verify that the type of a zod object is what you expect from the TS type,
 * e.g. z.infer<typeof zDocument> is the same as Document
 *
 * Verifying both directions:
 *
 * 		expectType<z.infer<typeof zDocument>>({} as Document);
 * 		expectType<Document>({} as z.infer<typeof zDocument>);
 *
 * @param { any} _ A {} cast to the type you want to verify
 * @returns { void } A noop function
 */
export function expectType(_) {
    /* noop */
}
expectType({});
expectType({});
export const UserRolesLookup = {
    User: 'user',
    Admin: 'admin',
    BusinessAdmin: 'businessAdmin',
    SiteAdmin: 'siteAdmin',
    ModerationAdmin: 'moderationAdmin'
};
export const FeatureFlagsLookup = {
    Feature1: 'feature1',
    Anonymous: 'anonymous',
    Subscription: 'subscription'
};
export const RolesLookup = {
    ...UserRolesLookup,
    ...FeatureFlagsLookup,
    Refresh: 'refresh',
    Reset: 'reset',
    EmailLogin: 'emailLogin',
    Unsubscribe: 'unsubscribe',
    Images: 'images',
    Subscriber: 'subscriber'
};
export const RolesEnum = z.nativeEnum(RolesLookup);
export const UserRolesEnum = z.nativeEnum(UserRolesLookup);
export const zServiceErrorContextFields = z.intersection(z.record(z.unknown()), z.object({
    context: z.string()
}));
expectType({});
expectType({});
export const zServiceErrorFields = z.object({
    errorCode: z.number(),
    errorMessage: z.string(),
    publicErrorMessage: z.string().optional(),
    ctx: zServiceErrorContextFields.optional()
});
expectType({});
expectType({});
// Enforce image type support
export function ImageTypes(type) {
    const newType = type;
    if (imageTypes.indexOf(newType) >= 0) {
        return newType;
    }
    else {
        return { errorCode: 415, errorMessage: 'Unsupported Image Type' };
    }
}
export const zEditable = (item) => {
    const type = z.object({
        history: z.array(item.omit({ history: true, _attachments: true })),
        editCount: z.number()
    });
    return item.merge(type);
};
export const CouchResponseFields = z.object({
    ok: z.boolean(),
    id: z.string(),
    rev: z.string()
});
export const zDummy = zDocument.extend({
    type: z.literal('dummy'),
    dummy: z.string(),
    dummy2: z.string()
});
expectType({});
expectType({});
// These a dehrydrated trees, pointers to the objects
export const zTreeFields = z.object({
    root: z.string(),
    rootType: DocumentTypesEnum,
    parent: z.string().optional(),
    parentType: DocumentTypesEnum,
    children: z.array(z.string())
});
// Validate zod schema matches
expectType({});
expectType({});
export function capitalize(s) {
    return (s[0].toUpperCase() + s.slice(1));
}
// Helper object for filtering arrays with null
export const isNotNull = (item) => item !== null;
export const isDocumentNew = (item) => item._id === '';
export const zFeedbackFields = z.object({
    feedback: z.literal(1).or(z.literal(0)).or(z.literal(-1))
});
/**
 * Are two Documents the same document?
 * @param { Document | null | undefined } a First document for comparison
 * @param { Document | null | undefined } b Second document for comparison
 * @returns { boolean } True if documents are the same document
 */
export function objectEquals(a, b) {
    if (!a || !b)
        return false;
    return a._id === b._id;
}
